// Apps → Update Batches: every batch with its pipeline progress, plus the
// New Batch modal. Batch status is automatic (see api/update-batches.ts).
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listUpdateBatches, createUpdateBatch, deleteUpdateBatch,
  type BatchStatus, type Candidate, type UpdateBatch,
} from '@/api/update-batches';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { AppIcon, BundlePicker, describePick, type PickResult } from '@/components/update-batches/BundlePicker';
import { BatchStatusBadge, STAGES, StageBar, StageCaption, StageLegend, fmtUnix, repoLabel, stageValue } from '@/components/update-batches/bits';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export function UpdateBatches() {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<BatchStatus | ''>('');
  const [showNew, setShowNew] = useState(false);

  const batchesQ = useQuery({
    queryKey: ['update-batches', status],
    queryFn: () => listUpdateBatches({ status }),
    refetchInterval: 60_000,
  });
  const batches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = batchesQ.data ?? [];
    return needle ? all.filter((b) => b.name.toLowerCase().includes(needle)) : all;
  }, [batchesQ.data, q]);

  const del = useMutation({
    mutationFn: deleteUpdateBatch,
    onSuccess: () => { toast('Batch deleted'); qc.invalidateQueries({ queryKey: ['update-batches'] }); },
    onError: (e: any) => toast(e?.message ?? 'Failed to delete batch'),
  });

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-content">Update Batches</h1>
        <p className="text-content-muted text-sm mt-1 max-w-3xl">
          Group the bundles you need to update into a named batch, then watch build, QA and publish status roll in
          automatically — no more tracking it by hand in a spreadsheet.
        </p>
        <div className="mt-2"><StageLegend /></div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div className="flex gap-2 flex-1">
          <input
            className="input sm:max-w-xs"
            placeholder="Search batches…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select value={status} options={STATUS_OPTIONS} onChange={(v) => setStatus(v as BatchStatus | '')} className="w-40" />
        </div>
        {canEdit && <Button onClick={() => setShowNew(true)}>+ New Batch</Button>}
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="text-left p-3 font-medium">Batch</th>
              <th className="text-left p-3 font-medium">Repo</th>
              <th className="text-left p-3 font-medium">Bundles</th>
              <th className="text-left p-3 font-medium">Pipeline progress</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {batchesQ.isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-content-subtle">Loading…</td></tr>
            )}
            {!batchesQ.isLoading && batches.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-content-subtle">No batches yet.</td></tr>
            )}
            {batches.map((b) => <BatchRow key={b.id} batch={b} canEdit={canEdit} onDelete={() => {
              if (confirm(`Delete batch “${b.name}”? Its bundles become free for a new batch.`)) del.mutate(b.id);
            }} />)}
          </tbody>
        </table>
      </div>

      {showNew && <NewBatchModal onClose={() => setShowNew(false)} />}
    </div>
  );
}

function BatchRow({ batch, canEdit, onDelete }: { batch: UpdateBatch; canEdit: boolean; onDelete: () => void }) {
  const p = batch.progress;
  return (
    <tr className="border-t border-border hover:bg-surface-muted/40">
      <td className="p-3 align-middle">
        <Link to={`/apps-setup/update-batches/${batch.id}`} className="font-medium text-content hover:text-primary">
          {batch.name}
        </Link>
        <div className="text-xs text-content-subtle mt-0.5">
          {batch.created_by_name || batch.created_by_email || '—'} · {fmtUnix(batch.created_at)}
        </div>
      </td>
      <td className="p-3 align-middle">
        <span className="inline-flex px-2 py-0.5 rounded bg-surface-muted text-xs text-content-muted" title={batch.repos.map((r) => r.name).join(', ')}>
          {repoLabel(batch.repos)}
        </span>
      </td>
      <td className="p-3 align-middle text-content">{batch.bundle_count}</td>
      <td className="p-3 align-middle">
        <div className="grid grid-cols-4 gap-3 min-w-[360px]">
          {STAGES.map((s) => {
            const v = stageValue(p, s.key);
            return (
              <div key={s.key}>
                <div className="text-[10px] uppercase tracking-wide text-content-subtle mb-1">{s.label}</div>
                <StageBar {...v} thin />
                <div className="text-xs mt-1 whitespace-nowrap"><StageCaption stage={v} /></div>
              </div>
            );
          })}
        </div>
      </td>
      <td className="p-3 align-middle"><BatchStatusBadge status={batch.status} /></td>
      <td className="p-3 align-middle text-right">
        {canEdit && (
          <button onClick={onDelete} className="text-xs text-danger hover:underline" title="Delete batch">Delete</button>
        )}
      </td>
    </tr>
  );
}

function NewBatchModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToastStore((s) => s.push);
  const [name, setName] = useState('');
  const [staged, setStaged] = useState<Candidate[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stagedIds = useMemo(() => new Set(staged.map((c) => c.id)), [staged]);

  const onPick = (r: PickResult) => {
    setStaged((cur) => [...cur, ...r.added.filter((c) => !cur.some((x) => x.id === c.id))]);
    setNotice(r.added.length === 1 && r.skipped.length === 0 ? `Added ${r.added[0]!.name}.` : `${describePick(r)}. Remove any you don't need below.`);
  };

  const create = useMutation({
    mutationFn: createUpdateBatch,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['update-batches'] });
      const busy = res.skipped.filter((s) => s.reason === 'busy');
      toast(busy.length ? `Batch created; ${busy.length} bundles skipped — already in another active batch` : 'Batch created');
      navigate(`/apps-setup/update-batches/${res.batch.id}`);
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to create batch'),
  });

  const submit = () => {
    setError(null);
    if (!name.trim()) { setError('Batch name is required'); return; }
    if (staged.length === 0) { setError('Add at least one bundle'); return; }
    create.mutate({ name: name.trim(), app_ids: staged.map((c) => c.id) });
  };

  return (
    <Modal isOpen onClose={onClose} title="New Update Batch" size="2xl">
      <div className="space-y-5">
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-content-subtle">Batch name</span>
          <input autoFocus className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Plinko Repo Refresh" />
        </label>

        <div>
          <div className="text-xs uppercase tracking-wide text-content-subtle mb-2">Add bundles</div>
          <BundlePicker onPick={onPick} excludeIds={stagedIds} />
        </div>

        {notice && (
          <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-content">✓ {notice}</div>
        )}

        <div className="rounded-lg border border-border">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border text-xs text-content-subtle">
            <span>Bundles in this batch</span>
            <span>{staged.length}</span>
          </div>
          {staged.length === 0 ? (
            <p className="p-4 text-sm text-content-subtle text-center">Nothing added yet.</p>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-border">
              {staged.map((c) => (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                  <AppIcon url={c.icon_url} name={c.name} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-content truncate">{c.name}</div>
                    <div className="text-xs font-mono text-content-subtle truncate">{c.bundle_id}</div>
                  </div>
                  <button
                    type="button"
                    className="text-content-subtle hover:text-danger px-1"
                    title="Remove from batch"
                    onClick={() => setStaged((cur) => cur.filter((x) => x.id !== c.id))}
                  >×</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && <div className="text-sm text-danger">{error}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={create.isPending}>Create Batch</Button>
        </div>
      </div>
    </Modal>
  );
}
